import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import HeroNew from '@/components/portfolio/HeroNew';
import AboutNew from '@/components/portfolio/AboutNew';
import ExperienceNew from '@/components/portfolio/ExperienceNew';
import Projects from '@/components/portfolio/Projects';
import SkillsNew from '@/components/portfolio/SkillsNew';
import Publications from '@/components/portfolio/Publications';
import Books from '@/components/portfolio/Books';
import Contact from '@/components/portfolio/Contact';
import { personalInfo } from '@/data/portfolioConfig';

export default function Home() {
  const { i18n } = useTranslation();
  const currentLang = i18n.language as 'it' | 'en';

  return (
    <>
      <Helmet>
        <title>Cristian Tesconi | PDT Lead & GenAI / AI Agent Engineer</title>
        <meta name="description" content={currentLang === 'it' ? 'Cristian Tesconi - PDT Lead in Dumarey Softronix, Ingegnere Robotico e dell\'Automazione. Integrazione di agenti AI e vibe engineering, esperto FMI/ASAM, autore di 6 libri tecnici. Torino, Italia.' : 'Cristian Tesconi - PDT Lead at Dumarey Softronix, Robotics & Automation Engineer. AI agent integration and vibe engineering, FMI/ASAM expert, author of 6 technical books. Turin, Italy.'} />
        <meta name="keywords" content="cristian tesconi, cristiantesconi, cristian tesconi ingegnere, cristian tesconi torino, cristian tesconi libri, cristian tesconi amazon, GenAI engineer, AI agent engineer, agenti AI, RAG, MCP, vibe engineering, PDT lead, team leader automotive, ingegnere simulazione, FMI standard, ASAM, co-simulazione FMU, SIL engineer, C++ Qt, robotics engineer, automation engineer" />
        <meta name="author" content="Cristian Tesconi" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="language" content={currentLang} />
        <meta name="geo.region" content="IT-TO" />
        <meta name="geo.placename" content="Torino, Italia" />
        <link rel="canonical" href="https://cristiantesconi.it" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cristiantesconi.it" />
        <meta property="og:title" content={`Cristian Tesconi - ${personalInfo.title[currentLang]}`} />
        <meta property="og:description" content={`${personalInfo.bio[currentLang]} - Expert in FMI, ASAM standards, and multi-physics simulation.`} />
        <meta property="og:site_name" content="Cristian Tesconi" />
        <meta property="og:locale" content={currentLang === 'it' ? 'it_IT' : 'en_US'} />
        <meta property="og:image" content="https://cristiantesconi.it/hero.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`Cristian Tesconi - ${personalInfo.title[currentLang]}`} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Cristian Tesconi - ${personalInfo.title[currentLang]}`} />
        <meta name="twitter:description" content={`${personalInfo.bio[currentLang]} - FMI & ASAM Simulation Expert`} />
        <meta name="twitter:image" content="https://cristiantesconi.it/hero.png" />
        <meta name="twitter:image:alt" content="Cristian Tesconi" />

        {/* Enhanced Structured Data - Person schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Cristian Tesconi",
            "givenName": "Cristian",
            "familyName": "Tesconi",
            "alternateName": ["cristian.tesconi", "cristiantesconi"],
            "jobTitle": "PDT Lead, Tools & Application Software | GenAI & AI Agent Engineer",
            "description": "Cristian Tesconi è un PDT Lead e Ingegnere Robotico e dell'Automazione presso Dumarey Softronix, con oltre 6 anni di esperienza. Integra agenti AI e vibe engineering nel processo di sviluppo, è formatore aziendale su Python e Git, esperto di standard FMI e ASAM, e autore di 6 libri tecnici.",
            "email": personalInfo.email,
            "telephone": personalInfo.phone,
            "url": "https://cristiantesconi.it",
            "image": "https://cristiantesconi.it/hero.png",
            "nationality": "Italian",
            "sameAs": [
              personalInfo.linkedin,
              personalInfo.github,
              "https://www.amazon.it/stores/author/B0CC29G6VV",
              "https://cristiantesconi.it"
            ],
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Torino",
              "addressRegion": "Piemonte",
              "addressCountry": "IT"
            },
            "worksFor": {
              "@type": "Organization",
              "name": "Dumarey Softronix"
            },
            "alumniOf": {
              "@type": "CollegeOrUniversity",
              "name": "Università di Pisa"
            },
            "knowsAbout": [
              "Robotics Engineering",
              "Automation Engineering",
              "FMI Standard (Functional Mock-up Interface)",
              "ASAM Standards",
              "Multi-Physics Simulation",
              "Co-Simulation",
              "C++ Programming",
              "Qt Framework",
              "Python Programming",
              "Automotive Software Development",
              "SIL Testing",
              "Control Systems",
              "Embedded Systems",
              "Artificial Intelligence",
              "Machine Learning",
              "Generative AI",
              "AI Agents",
              "Retrieval-Augmented Generation (RAG)",
              "Model Context Protocol (MCP)",
              "Vibe Engineering"
            ],
            "hasOccupation": {
              "@type": "Occupation",
              "name": "PDT Lead, Tools & Application Software",
              "occupationLocation": {
                "@type": "City",
                "name": "Turin"
              },
              "skills": "FMI, ASAM, C++, Qt6, Python, SIL Testing, Automotive Simulation, AI Agents, RAG, MCP, GenAI"
            }
          })}
        </script>

        {/* BreadcrumbList for SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://cristiantesconi.it"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "About",
                "item": "https://cristiantesconi.it#about"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Experience",
                "item": "https://cristiantesconi.it#experience"
              },
              {
                "@type": "ListItem",
                "position": 4,
                "name": "Skills",
                "item": "https://cristiantesconi.it#skills"
              },
              {
                "@type": "ListItem",
                "position": 5,
                "name": "Publications",
                "item": "https://cristiantesconi.it#publications"
              }
            ]
          })}
        </script>
        {/* WebSite schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Cristian Tesconi",
            "url": "https://cristiantesconi.it",
            "description": currentLang === 'it'
              ? "Sito ufficiale di Cristian Tesconi - PDT Lead e Ingegnere Robotico e dell'Automazione. Integrazione di agenti AI e vibe engineering, esperto FMI/ASAM, autore di 6 libri tecnici."
              : "Official site of Cristian Tesconi - PDT Lead and Robotics & Automation Engineer. AI agent integration and vibe engineering, FMI/ASAM expert, author of 6 technical books.",
            "inLanguage": ["it", "en"],
            "author": { "@type": "Person", "name": "Cristian Tesconi" }
          })}
        </script>
      </Helmet>

      <div>
        <HeroNew />
        <AboutNew />
        <ExperienceNew />
        <Projects />
        <SkillsNew />
        <Publications />
        <Books />
        <Contact />
      </div>
    </>
  );
}
